import { compact, intersection, isEmpty, omit, partition, pickBy, without } from 'lodash-es'
import { createSelector } from 'reselect'

import { NON_EXCLUSIVE_LOCATIONS, PHONE_CALL_KINDS } from './constants'

const phoneKinds = Object.values(PHONE_CALL_KINDS)
const hasInboundPhone = (kinds) => kinds.includes(PHONE_CALL_KINDS.inbound)
const hasOutboundPhone = (kinds) => kinds.includes(PHONE_CALL_KINDS.outbound)

const locationsSelector = (state) => state.locations

export const potentialKindSelector = (state) => state.potentialKind

const isCollectiveSelector = (state) => state.isCollective

const integrationsSelector = (state) => state.integrations

export const possibleKindsSelector = (state) => state.possibleKinds

export const isDisabledSelector = (state) => state.isDisabled

export const isNewEventTypeSelector = (state) => state.isNewEventType

export const isPendingSelector = (state) => state.isPending

export const createIsNewlyAddedLocationSelector = (locationId) => (state) => state.lastAddedLocationId === locationId

export const useNewDropdownSelector = ({ useNewDropdown }) => useNewDropdown

export const useNewLocationSelectSelector = ({ useNewLocationSelect }) => useNewLocationSelect

export const activeLocationsSelector = createSelector(
  locationsSelector,
  (locations) => pickBy(locations, ({ _destroy }) => !_destroy)
)

export const selectedLocationsSelector = createSelector(
  locationsSelector,
  (locations) => pickBy(locations, ({ kind }) => !isEmpty(kind))
)

export const disabledTooltipSelector = (state) => state.disabledTooltip

export const popupSelector = (state) => state.locationPopup

export const showIntegrationErrorsSelector = (state) => state.showIntegrationErrors

const showLocationVisibilityOptionsSelector = (state) => state.showLocationVisibilityOptions

export const sortedActiveLocationsSelector = createSelector(
  activeLocationsSelector,
  (locations) => (
    Object.entries(locations).sort(([, currValue], [, nextVal]) => (
      currValue.position - nextVal.position
    ))
  )
)

const editedLocationIdSelector = createSelector(
  popupSelector,
  ({ locationId }) => locationId
)

const selectedPopupKindSelector = createSelector(
  popupSelector,
  ({ data }) => data.kind
)

const squashedPhoneKindsSelector = createSelector(
  possibleKindsSelector,
  selectedPopupKindSelector,
  (possibleKinds, selectedPopupKind) => {
    if (phoneKinds.includes(selectedPopupKind)) {
      const extraPhoneKind = possibleKinds.find((kind) => (
        phoneKinds.includes(kind)
        && selectedPopupKind !== kind
      ))
      return without(possibleKinds, extraPhoneKind)
    }

    if (hasInboundPhone(possibleKinds) && hasOutboundPhone(possibleKinds)) {
      return without(possibleKinds, PHONE_CALL_KINDS.inbound)
    }

    return possibleKinds
  }
)

const takenKindsSelector = createSelector(
  activeLocationsSelector,
  editedLocationIdSelector,
  selectedPopupKindSelector,
  (activeLocations, editedLocationId, selectedPopupKind) => {
    const takenLocations = omit(activeLocations, editedLocationId)
    let takenKinds = Object.values(takenLocations).map(({ kind }) => kind)
    if (hasInboundPhone(takenKinds) || hasOutboundPhone(takenKinds)) {
      takenKinds = [...takenKinds, ...phoneKinds]
    }

    takenKinds = without(takenKinds, selectedPopupKind)
    return new Set(takenKinds)
  }
)

export const availableLocationKindsSelector = createSelector(
  squashedPhoneKindsSelector,
  takenKindsSelector,
  (possibleKinds, takenKinds) => (
    possibleKinds.filter((kind) => !takenKinds.has(kind) || NON_EXCLUSIVE_LOCATIONS.includes(kind))
  )
)

export const dividedAvailableLocationKindsSelector = createSelector(
  availableLocationKindsSelector,
  (possibleKinds) => {
    const [conferenceLocations, standardLocations] = partition(possibleKinds, (kind) => kind.includes('conference'))
    return standardLocations
      .map((location) => ({ type: 'location', value: location }))
      .concat([{ type: 'divider', value: 'web_conferencing' }])
      .concat(conferenceLocations.map((location) => ({ type: 'location', value: location })))
  }
)

export const enabledIntegrationsSelector = createSelector(
  integrationsSelector,
  (integrations) => Object.entries(integrations).reduce((acc, [integration, isEnabled]) => {
    if (isEnabled) {
      return [...acc, integration]
    }
    return acc
  }, [])
)

export const nextPositionSelector = createSelector(
  sortedActiveLocationsSelector,
  (sortedActiveLocations) => sortedActiveLocations.length
)

const activeSelectedLocationIdsSelector = createSelector(
  activeLocationsSelector,
  selectedLocationsSelector,
  (activeLocations, selectedLocations) => (
    intersection(
      Object.keys(activeLocations),
      Object.keys(selectedLocations)
    )
  )
)

const activeSelectedLocationSelector = createSelector(
  locationsSelector,
  activeSelectedLocationIdsSelector,
  (locations, activeSelectedLocationIds) => {
    const filteredLocations = Object.entries(locations).map(([key, location]) => (
      activeSelectedLocationIds.includes(key) && location
    ))

    return compact(filteredLocations)
  }
)

const firstSelectedLocationSelector = createSelector(
  activeSelectedLocationSelector,
  (activeSelectedLocations) => activeSelectedLocations[0]
)

const isCalendarDependentLocationSelector = createSelector(
  firstSelectedLocationSelector,
  (selectedLocation) => selectedLocation && ['google_conference', 'microsoft_teams_conference'].includes(selectedLocation.kind)
)

export const canHideLocationSelector = createSelector(
  editedLocationIdSelector,
  showLocationVisibilityOptionsSelector,
  activeSelectedLocationIdsSelector,
  (locationId, showLocationVisibilityOptions, activeSelectedLocationIds) => {
    if (!showLocationVisibilityOptions) {
      return false
    }
    return isEmpty(without(activeSelectedLocationIds, locationId))
  }
)

export const showDependentNoticeSelector = createSelector(
  isCollectiveSelector,
  isCalendarDependentLocationSelector,
  (isCollective, isCalendarDependentLocation) => isCollective && isCalendarDependentLocation
)
